import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { EventBus, GameEvents } from '@/core/EventBus';

/**
 * Post-processing effects manager
 * Handles EffectComposer with RenderPass and OutlinePass for toon-style outlines
 */
export class PostProcessor {
  private composer: EffectComposer;
  private outlinePass: OutlinePass;
  private renderPass: RenderPass;
  private renderer: THREE.WebGLRenderer;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    eventBus: EventBus
  ) {
    this.renderer = renderer;

    // Create composer
    this.composer = new EffectComposer(renderer);

    // Add render pass (renders the scene normally first)
    this.renderPass = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass);

    // Add outline pass for edge detection
    const size = new THREE.Vector2(window.innerWidth, window.innerHeight);
    this.outlinePass = new OutlinePass(size, scene, camera);
    this.configureOutlinePass();
    this.composer.addPass(this.outlinePass);

    // Add output pass for correct color space
    const outputPass = new OutputPass();
    this.composer.addPass(outputPass);

    this.setupResizeHandler(eventBus);
  }

  /**
   * Configure outline pass settings for clean 2D look
   */
  private configureOutlinePass(): void {
    // Edge detection settings for ~2px black outline
    this.outlinePass.edgeStrength = 10.0;
    this.outlinePass.edgeGlow = 0.0; // No glow for clean 2D look
    this.outlinePass.edgeThickness = 2.0;

    // Outline colors
    this.outlinePass.visibleEdgeColor.set(0x000000); // Black outlines
    this.outlinePass.hiddenEdgeColor.set(0x000000); // Consistent color for hidden edges
  }

  /**
   * Handle window resize
   */
  private setupResizeHandler(eventBus: EventBus): void {
    eventBus.on(GameEvents.WINDOW_RESIZED, () => {
      this.updateSize();
    });
  }

  /**
   * Update composer and pass sizes
   */
  updateSize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const pixelRatio = this.renderer.getPixelRatio();

    this.composer.setSize(width, height);
    this.outlinePass.resolution.set(width * pixelRatio, height * pixelRatio);
  }

  /**
   * Update the camera reference (for when camera changes)
   */
  updateCamera(camera: THREE.Camera): void {
    this.renderPass.camera = camera;
    this.outlinePass.renderCamera = camera;
  }

  /**
   * Set objects to outline
   * @param objects Array of Object3D to apply outlines to
   */
  setOutlineObjects(objects: THREE.Object3D[]): void {
    this.outlinePass.selectedObjects = objects;
  }

  /**
   * Add objects to the outline list
   */
  addOutlineObjects(objects: THREE.Object3D[]): void {
    this.outlinePass.selectedObjects.push(...objects);
  }

  /**
   * Clear all outlined objects
   */
  clearOutlineObjects(): void {
    this.outlinePass.selectedObjects = [];
  }

  /**
   * Render the scene with post-processing
   */
  render(): void {
    this.composer.render();
  }

  /**
   * Get the outline pass for external configuration
   */
  getOutlinePass(): OutlinePass {
    return this.outlinePass;
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.composer.dispose();
  }
}
